import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMessageTemplates,
  saveMessageTemplate,
  sendOutreach,
  type OutreachTemplate,
  type OutreachButton,
} from '../api/client';

// Render Telegram-HTML (a safe subset — <b>/<i>/<a> + newlines) into the
// preview bubble. Operator-authored, admin-only context (same trust level as
// the report HTML rendered on ChatDetail).
function toPreviewHtml(bodyHtml: string): string {
  return bodyHtml.replace(/\{\{\s*projectName\s*\}\}/g, 'Acme DAO').replace(/\n/g, '<br/>');
}

function buttonLabel(b: OutreachButton): string {
  if (b.kind === 'help') return `${b.text}  ·  ticket`;
  if (b.kind === 'survey') return `${b.text}  ·  survey`;
  return `${b.text}  ·  ${b.action}`;
}

function Bubble({ tmpl }: { tmpl: { bodyHtml: string; buttons: OutreachButton[] } }) {
  return (
    <div className="rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm ring-1 ring-gray-200">
      <div
        className="text-sm leading-relaxed text-gray-800"
        dangerouslySetInnerHTML={{ __html: toPreviewHtml(tmpl.bodyHtml) }}
      />
      {tmpl.buttons.length > 0 && (
        <div className="mt-3 flex flex-col gap-1.5">
          {tmpl.buttons.map((b, i) => (
            <div key={i} className="rounded-md bg-sky-50 px-3 py-1.5 text-center text-xs font-medium text-sky-700 ring-1 ring-sky-100">
              {b.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type Draft = { title: string; bodyHtml: string; enabled: boolean; upsell: boolean };

export function BotMessagesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['message-templates'], queryFn: getMessageTemplates, staleTime: 60_000 });

  const templates = q.data?.templates ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [testChatId, setTestChatId] = useState('');

  // Default selection = first template once data lands.
  useEffect(() => {
    if (!selectedKey && templates.length > 0) setSelectedKey(templates[0].key);
  }, [templates, selectedKey]);

  const selected = templates.find((t) => t.key === selectedKey) || null;

  // Sync the draft when the selected template changes (or first loads).
  useEffect(() => {
    if (selected) setDraft({ title: selected.title, bodyHtml: selected.bodyHtml, enabled: selected.enabled, upsell: selected.upsell });
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: (t: OutreachTemplate) =>
      saveMessageTemplate(t.key, { title: draft!.title, bodyHtml: draft!.bodyHtml, buttons: t.buttons, enabled: draft!.enabled, upsell: draft!.upsell }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['message-templates'] }),
  });

  const sendMut = useMutation({
    mutationFn: (vars: { chatId: string; key: string }) => sendOutreach(vars.chatId, vars.key),
  });

  const dirty =
    !!selected && !!draft &&
    (draft.bodyHtml !== selected.bodyHtml || draft.enabled !== selected.enabled || draft.upsell !== selected.upsell || draft.title !== selected.title);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Bot Messages</h1>
        <p className="mt-1 text-sm text-gray-500">
          Per-segment outreach templates. Edit copy, toggle a segment on, and preview the Telegram message — no deploy needed.
        </p>
      </div>

      {/* Master switch banner */}
      {q.data && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            q.data.masterEnabled
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          {q.data.masterEnabled ? (
            <>
              <b>Outreach is LIVE.</b> Scheduled sends deliver to users for every enabled segment.
            </>
          ) : (
            <>
              <b>Outreach is paused</b> (master switch <code className="rounded bg-amber-100 px-1">OUTREACH_ENABLED=false</code>).
              Scheduled runs are dry-run only. A <b>manual send</b> below still delivers — use it to test on a staging group.
            </>
          )}
        </div>
      )}

      {q.isLoading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_1fr]">
          {/* Template list */}
          <aside className="space-y-1">
            {templates.map((t) => (
              <button
                key={t.key}
                onClick={() => setSelectedKey(t.key)}
                className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm ${
                  t.key === selectedKey ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <span className="text-gray-800">{t.label}</span>
                <span className="flex items-center gap-1">
                  {t.upsell && <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">upsell</span>}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${t.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {t.enabled ? 'on' : 'off'}
                  </span>
                </span>
              </button>
            ))}
          </aside>

          {/* Editor + preview */}
          {selected && draft && (
            <section className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">{selected.label}</h2>
                <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{selected.key}</code>
                <span className={`rounded-full px-2 py-0.5 text-xs ${selected.source === 'override' ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>
                  {selected.source === 'override' ? 'customized' : 'default'}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Left: editor */}
                <div className="space-y-3">
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Message (Telegram HTML)</label>
                  <textarea
                    value={draft.bodyHtml}
                    onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
                    rows={9}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed"
                  />
                  <p className="text-xs text-gray-400">
                    Variables: <code className="rounded bg-gray-100 px-1">{'{{projectName}}'}</code>. Tags: <code className="rounded bg-gray-100 px-1">&lt;b&gt;</code> <code className="rounded bg-gray-100 px-1">&lt;i&gt;</code> <code className="rounded bg-gray-100 px-1">&lt;a&gt;</code>. Keep it kind — no plan numbers.
                  </p>

                  <div className="flex flex-wrap gap-4 pt-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                      Enabled <span className="text-xs text-gray-400">(scheduled sends)</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={draft.upsell} onChange={(e) => setDraft({ ...draft, upsell: e.target.checked })} />
                      Upsell <span className="text-xs text-gray-400">(skips PRO chats)</span>
                    </label>
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <button
                      onClick={() => saveMut.mutate(selected)}
                      disabled={!dirty || saveMut.isPending}
                      className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saveMut.isPending ? 'Saving…' : 'Save'}
                    </button>
                    {saveMut.isSuccess && !dirty && <span className="text-xs text-emerald-700">Saved ✓</span>}
                    {saveMut.isError && <span className="text-xs text-red-600">Save failed</span>}
                  </div>

                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                    Buttons are wired in code (survey options, help ticket, CTAs) and shown in the preview. Editing button copy comes later.
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {selected.buttons.map((b, i) => (
                        <span key={i} className="rounded bg-white px-2 py-0.5 ring-1 ring-gray-200">{buttonLabel(b)}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: preview + manual send */}
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Preview</div>
                    <div className="rounded-xl bg-[#e7ebf0] p-4">
                      <Bubble tmpl={{ bodyHtml: draft.bodyHtml, buttons: selected.buttons }} />
                    </div>
                  </div>

                  <div className="rounded-md border border-gray-200 bg-white p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Manual send (test)</div>
                    <p className="mt-1 text-xs text-gray-400">
                      Delivers this template to one chat now, bypassing the schedule + frequency cap. Respects opt-out. Save first if you edited.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <input
                        value={testChatId}
                        onChange={(e) => setTestChatId(e.target.value)}
                        placeholder="chat id, e.g. -100123…"
                        className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-mono"
                      />
                      <button
                        onClick={() => sendMut.mutate({ chatId: testChatId.trim(), key: selected.key })}
                        disabled={!testChatId.trim() || sendMut.isPending}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        {sendMut.isPending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                    {sendMut.data && (
                      <div className={`mt-2 text-xs ${sendMut.data.sent ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {sendMut.data.sent ? `Sent to ${sendMut.data.target} ✓` : `Not sent: ${sendMut.data.skip || sendMut.data.error || 'unknown'}`}
                      </div>
                    )}
                    {sendMut.isError && <div className="mt-2 text-xs text-red-600">Request failed</div>}
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
